import Vue from 'vue'
import Router from 'vue-router'
import { store } from '@/store/store'
import { wrapRouter } from 'oaf-vue-router'

import Reauth from './../views/auth/ReAuthView'
import Login from '@/views/auth/LoginView'
import UnknownDeviceView from '@/views/auth/UnknownDeviceView'
import RequestResetPassword from '@/views/auth/ForgotPasswordView'
import Seal from '@/views/public/SealView'
import InvitationView from '@/views/public/InvitationView'
import FeedbackView from '@/views/public/FeedbackView'
// import HostingAsr from './../components/Services/Hosting/HostingAsr'

import { isKvmPath } from '@/helpers/serverPaths'
import servicesRouter from './services'
import domainRouter from './domain'
import support from './support'
import billing from './billing'
import profileRouter from './profile'
import sslRouter from './ssl'
import crRouter from './cancel'
import promoRouter from './promo'
import wizardRouter from './wizard'
import affiliatesRouter from './affiliates'
import notificationsRouter from './notifications'
import alertsRouter from './alerts'
import downtimeRouter from './downtime'
import migrationsRouter from './migrations'
import websitesRouter from './websites'
import applicationsRouter from './applications'
import emailRouter from './email'
import renewRouter from './renew'

// import PageNotFound from '@/views/errors/PageNotFound'
import Layout from '@/container/Full'
import AccountManagerDashboard from '@/views/dashboard/AccountManagerDashboard'

import { getCookie } from 'tiny-cookie'
import { ExternalLinks } from '@/consts/main'
import { SERVICE_TYPES } from '@/consts/service'
// import { store } from '@/store/store'

Vue.use(Router)

export const mainRoutes = [
  {
    path: '*',
    redirect: '/'
  },
  {
    path: '/login',
    component: Login,
    name: 'login',
    meta: {
      access_type: 'unauthorized',
      pageTitle: 'Login',
    },
  },
  {
    path: '/unknown-device/:hash',
    component: UnknownDeviceView,
    name: 'unknown_device',
    props: true,
    meta: {
      access_type: 'unauthorized',
      pageTitle: 'Unknown Device Login',
    },
  },
  // :hash? is optional parameter
  {
    path: '/reset-password/:hash?',
    component: RequestResetPassword,
    name: 'request_reset_password',
    props: true,
    meta: {
      access_type: 'unauthorized',
      pageTitle: 'Password Recovery',
    }
  },
  {
    path: '/invitation/accept/:hash',
    component: InvitationView,
    name: 'public_invitation',
    props: true,
    meta: {
      access_type: 'unauthorized',
      pageTitle: 'Invitation',
    }
  },
  {
    path: '/seal',
    component: Seal,
    name: 'seal',
    meta: {
      access_type: 'both',
      pageTitle: 'Seal',
      customPage: true, // this page does not support vuetify
      skipSession: true, // this option allows to skip initializeSession call
    },
  },
  {
    path: '/feedback/:link_hash/:score?',
    component: FeedbackView,
    name: 'feedback',
    meta: {
      access_type: 'both',
      pageTitle: 'Feedback',
      customPage: true, // this page does not support vuetify
      skipSession: true, // this option allows to skip initializeSession call
    },
  },
  {
    path: '/',
    component: Layout,
    children: [
      {
        path: '/',
        component: AccountManagerDashboard,
        exact: true,
        name: 'dashboard',
        meta: {
          breadcrumb: 'Dashboard',
          title: 'message.dashboard',
          pageTitle: 'Dashboard',
          access_type: 'authorized'
        }
      },
    ],
  },
  // {
  //   path: '/hosting/asr/:id',
  //   name: 'hosting_asr',
  //   component: HostingAsr,
  //   props: true
  // },
  {
    path: '/reauth/:sid',
    props: true,
    name: 'reauth',
    component: Reauth,
    meta: {
      access_type: 'both',
      skipSession: true // AM-217 multiple getsession requests fix
    }
  },
]

const router = new Router({
  mode: 'history',
  routes: [
    ...mainRoutes,
    ...support,
    ...billing,
    ...profileRouter,
    ...servicesRouter,
    ...domainRouter,
    ...sslRouter,
    ...crRouter,
    ...migrationsRouter,
    ...promoRouter,
    ...wizardRouter,
    ...affiliatesRouter,
    ...notificationsRouter,
    ...alertsRouter,
    ...downtimeRouter,
    ...websitesRouter,
    ...applicationsRouter,
    ...emailRouter,
    ...renewRouter,
  ]
})

wrapRouter(router, {
  renderTimeout: 300,
  primaryFocusTarget: '#focus-target, main h1, [role=main] h1',
  announcePageNavigation: false
})

router.beforeEach(async (to, from, next) => {
  // Read auth cookie
  const isAuthCookieAvailable = getCookie('sid')
  // set page title
  let title = to.meta.pageTitle ? `${to.meta.pageTitle} - GreenGeeks®` : `GreenGeeks®`
  const { hostingid: hostingId } = to.params
  // The KVM server area — /servers (list), /servers/order and /servers/vps/:id…
  // — but NOT /servers/mvm/:id (the existing VPS product, a normal billing
  // service). KVM has no billing-service detail record, so skip the getHosting
  // lookup here — it would throw UNK_SRV and abort navigation (blank page).
  const isKvmRoute = isKvmPath(to.path)
  if (hostingId && !isKvmRoute) {
    let { hosting } = store.getters
    if (!hosting.id) {
      hosting = await store.dispatch('getHosting', hostingId)
    }
    let domain = hosting.domain
    if (to.query.user && hosting.type === SERVICE_TYPES.RESELLER && store.getters.resellerSubAccounts.length) {
      const subAccountDomain = store.getters.resellerSubAccounts.find((item) => {
        return item.user === to.query.user
      })?.domain
      if (subAccountDomain) {
        domain = subAccountDomain
      }
    }
    title = `${domain} - ${title}`
  }
  document.title = title

  if (isAuthCookieAvailable === null) {
    if (to.name === 'cancel' && !to.params.service_id) {
      window.location.href = ExternalLinks.GG_CANCELLATION_KB
      return
    }
    switch (to.meta.access_type) {
      case 'authorized':
        let name = to?.name || 'dashboard'
        if (to.fullPath) {
          name = to.fullPath
        }
        localStorage.setItem('redirect', name)
        next('/login')
        break
      case 'unauthorized':
        next()
        break
      case 'both':
        next()
        break
    }
  } else {
    switch (to.meta.access_type) {
      case 'authorized':
        next()
        break
      case 'unauthorized':
        next('/')
        break
      case 'both':
        next()
        break
    }
  }
})

export default router
